import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, ChevronRight, Clock, Lock } from 'lucide-react';
import { useAuth, LanguageToggle, API } from '../App';
import Navbar from '../components/Navbar';
import axios from 'axios';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [videosByCategory, setVideosByCategory] = useState({});
  const [siteSettings, setSiteSettings] = useState(null);
  const [contentPage, setContentPage] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [heroImageReady, setHeroImageReady] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locale = i18n.language;
        const [catRes, featRes, vidRes, settingsRes, contentRes] = await Promise.all([
          axios.get(`${API}/categories?locale=${locale}`),
          axios.get(`${API}/videos/featured?locale=${locale}`),
          axios.get(`${API}/videos?locale=${locale}`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/content/pages/homepage?locale=${locale}`).catch(() => ({ data: null }))
        ]);
        
        setCategories(catRes.data);
        setFeaturedVideos(featRes.data);
        setSiteSettings(settingsRes.data);
        setContentPage(contentRes.data);
        setSettingsLoaded(true);
        
        // Group videos by category
        const grouped = {};
        vidRes.data.forEach(video => {
          if (!grouped[video.category_id]) {
            grouped[video.category_id] = [];
          }
          grouped[video.category_id].push(video);
        });
        setVideosByCategory(grouped);
      } catch (e) {
        console.error('Failed to fetch data', e);
        setSettingsLoaded(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [i18n.language]);

  const heroVideo = featuredVideos[0];
  const sections = contentPage?.sections || {};
  const heroContent = sections.hero || {};
  const positioning = sections.positioning || {};
  const programs = sections.programs || {};
  
  // Get hero content based on language
  const heroImageUrl = settingsLoaded ? (siteSettings?.hero_image_url || '') : '';
  const showHeroImage = Boolean(heroImageUrl && heroImageReady);
  const heroTitle = heroContent.title || (i18n.language === 'el'
    ? (siteSettings?.hero_title_el || t('hero.title'))
    : (siteSettings?.hero_title_en || t('hero.title')));
  const heroSubtitle = heroContent.subtitle || (i18n.language === 'el'
    ? (siteSettings?.hero_subtitle_el || t('hero.subtitle'))
    : (siteSettings?.hero_subtitle_en || t('hero.subtitle')));

  useEffect(() => {
    if (!heroImageUrl) {
      setHeroImageReady(false);
      return undefined;
    }

    let cancelled = false;
    setHeroImageReady(false);
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setHeroImageReady(true);
    };
    image.onerror = () => {
      if (!cancelled) setHeroImageReady(false);
    };
    image.src = heroImageUrl;

    return () => {
      cancelled = true;
    };
  }, [heroImageUrl]);

  return (
    <div className="min-h-screen bg-misel-bg">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85svh] w-full overflow-hidden bg-gradient-to-br from-black via-misel-bg to-[#180606]">
        {showHeroImage && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('${heroImageUrl}')`
            }}
          />
        )}
        {!showHeroImage && (
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(185,28,28,0.22),transparent_38%),linear-gradient(135deg,rgba(255,255,255,0.04),transparent_45%)]" />
        )}
        <div className="hero-gradient pointer-events-none absolute inset-0" />
        
        <div className="relative z-10 min-h-[85svh] flex flex-col justify-end pt-32 sm:pt-36 lg:pt-40 pb-24 px-8 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 max-w-2xl"
          >
            <span className="text-primary font-roboto font-bold text-sm tracking-[0.3em] uppercase mb-4 block">
              {heroContent.kicker || t('hero.tagline')}
            </span>
            <h1 className="font-roboto font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-4 tracking-wide text-shadow break-words">
              {heroTitle}
            </h1>
            <p className="text-lg text-gray-300 mb-8 whitespace-pre-line break-words">
              {heroSubtitle}
            </p>
            {(heroContent.body || heroContent.description) && (
              <p className="text-sm md:text-base text-gray-400 mb-8 max-w-xl whitespace-pre-line break-words">
                {heroContent.body || heroContent.description}
              </p>
            )}
            <div className="flex flex-wrap gap-4">
              <Link
                to={user ? "/categories" : (heroContent.primary_cta_url || "/register")}
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center gap-2"
              >
                <Play size={18} />
                {heroContent.primary_cta_label || t('hero.cta_primary')}
              </Link>
              <Link
                to={heroContent.secondary_cta_url || "/pricing"}
                data-testid="hero-cta-secondary"
                className="btn-secondary inline-flex items-center gap-2"
              >
                {heroContent.secondary_cta_label || t('hero.cta_secondary')}
                <ChevronRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {(positioning.title || positioning.body || (programs.items || []).length > 0) && (
        <section className="py-12 px-8 md:px-16 border-b border-white/5">
          <div className="max-w-5xl">
            {positioning.title && (
              <h2 className="font-roboto font-bold text-2xl md:text-3xl text-white tracking-wide mb-4 break-words">
                {positioning.title}
              </h2>
            )}
            {positioning.body && <p className="text-gray-400 max-w-3xl whitespace-pre-line break-words">{positioning.body}</p>}
            {(programs.items || []).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                {(programs.items || []).map((item, index) => (
                  <div key={`${item.title || 'program'}-${index}`} className="border border-white/10 bg-misel-card rounded-sm p-4">
                    <h3 className="text-white font-medium mb-2 break-words">{item.title}</h3>
                    <p className="text-gray-500 text-sm whitespace-pre-line break-words">{item.body || item.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-16 px-8 md:px-16">
        <div className="flex justify-between items-center mb-8">
          <h2 className="font-roboto font-bold text-2xl md:text-3xl text-white tracking-wide">
            {t('categories.title')}
          </h2>
          <Link 
            to="/categories" 
            className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
            data-testid="view-all-categories"
          >
            {t('categories.view_all')}
            <ChevronRight size={16} />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} />
          ))}
        </div>
      </section>

      {/* Videos by Category */}
      {categories.map((category) => {
        const videos = videosByCategory[category.id] || [];
        if (videos.length === 0) return null;
        
        return (
          <section key={category.id} className="py-8 px-8 md:px-16">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-roboto font-bold text-xl md:text-2xl text-white tracking-wide">
                {category.title}
              </h2>
              <Link 
                to={`/category/${category.slug}`}
                className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
              >
                {t('categories.view_all')}
                <ChevronRight size={16} />
              </Link>
            </div>
            
            <div className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide snap-x">
              {videos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </section>
        );
      })}

      {/* Footer */}
      <footer className="py-12 px-8 md:px-16 border-t border-white/10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © 2026 MISEL GROUP DIGITAL LAB. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/pricing" className="text-gray-400 hover:text-white text-sm transition-colors">
              {t('nav.pricing')}
            </Link>
            <Link to="/categories" className="text-gray-400 hover:text-white text-sm transition-colors">
              {t('nav.categories')}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const CategoryCard = ({ category, index }) => {
  const categoryImages = {
    cutting: 'https://images.pexels.com/photos/15659458/pexels-photo-15659458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    color: 'https://images.pexels.com/photos/7388933/pexels-photo-7388933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    styling: 'https://images.pexels.com/photos/14615064/pexels-photo-14615064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  };
  
  const imageUrl = category.image_url || categoryImages[category.slug] || categoryImages.cutting;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link
        to={`/category/${category.slug}`}
        data-testid={`category-card-${category.slug}`}
        className="block aspect-[3/4] relative overflow-hidden rounded-sm group cursor-pointer"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
          style={{ backgroundImage: `url('${imageUrl}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 card-hover-gradient" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="font-roboto font-bold text-xl text-white tracking-wide mb-2 group-hover:text-white transition-colors">
            {category.title}
          </h3>
          <p className="text-gray-400 text-sm line-clamp-2 group-hover:text-gray-200 transition-colors">
            {category.description}
          </p>
        </div>
      </Link>
    </motion.div>
  );
};

const VideoCard = ({ video }) => {
  const { t } = useTranslation();
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} ${t('common.min')}`;
  };

  return (
    <Link
      to={`/video/${video.slug}`}
      data-testid={`video-card-${video.slug}`}
      className="flex-shrink-0 w-72 snap-start"
    >
      <div className="aspect-video relative overflow-hidden rounded-sm group card-scale bg-misel-card border border-white/5">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: video.thumbnail_url 
              ? `url('${video.thumbnail_url}')` 
              : `url('https://images.pexels.com/photos/15659458/pexels-photo-15659458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
          }}
        />
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
        
        {/* Play button overlay */}
        {video.has_access && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center">
              <Play size={24} className="text-white ml-1" fill="white" />
            </div>
          </div>
        )}
        
        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded-sm text-xs text-white flex items-center gap-1">
          <Clock size={12} />
          {formatDuration(video.duration_seconds)}
        </div>
        
        {/* Lock icon if no access */}
        {!video.has_access && (
          <div className="absolute top-2 right-2">
            <Lock size={16} className="text-white/70" />
          </div>
        )}
      </div>
      
      <div className="mt-3">
        <h4 className="text-white font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
          {video.title}
        </h4>
      </div>
    </Link>
  );
};

export default HomePage;
